// PoolScreener core — the six screens + the composite score. Each screen is
// a pure function taking already-fetched numbers (the data layer does the
// I/O) and returning a uniform ScreenResult. All thresholds come straight
// from the spec; the fixtures in screens.test.ts pin the formulas.
import {
  annualizeVol,
  coefficientOfVariation,
  lambertIvDaily,
  lvrAnnual,
  lvrDaily,
  maxSafeLeverageForShort,
  mean,
  realizedVolDaily,
  shortLiquidationPrice,
} from './math';
import {
  FUNDING_INTERVALS_PER_YEAR,
  MIN_DAILY_SAMPLES,
  TRADING_DAYS_PER_YEAR,
  type ScreenResult,
} from './types';

// ── Screen 1 — Traffic vs. Jumpiness (LVR breakeven) ───────────────────
export interface Screen1Input {
  dailyVolume: number;
  feeTier: number; // fraction, e.g. 0.003
  tvl: number;
  sigmaDaily: number; // realized daily vol, from Screen 3
}

/** dailyFeeReturn = volume·fee/TVL vs dailyLVR = σ²/8. PASS iff fees win.
 *  coverageRatio = feeReturn/LVR is the primary ranking metric. */
export function screen1LvrBreakeven(i: Screen1Input): ScreenResult {
  if (i.tvl <= 0 || i.sigmaDaily <= 0) {
    return { value: 0, verdict: 'INSUFFICIENT_DATA', detail: {} };
  }
  const dailyFeeReturn = (i.dailyVolume * i.feeTier) / i.tvl;
  const dailyLVR = lvrDaily(i.sigmaDaily);
  const coverageRatio = dailyLVR > 0 ? dailyFeeReturn / dailyLVR : 0;
  return {
    value: coverageRatio,
    verdict: dailyFeeReturn > dailyLVR ? 'PASS' : 'FAIL',
    detail: { dailyFeeReturn, dailyLVR, coverageRatio },
  };
}

// ── Screen 2 — Flow Toxicity Proxy ─────────────────────────────────────
export interface Screen2Input {
  cexListed: boolean; // volatile asset on Binance perps/spot?
  dailyVolumes: number[]; // 30d daily volume series
}

/** WARN (not FAIL) when the asset trades on a CEX *and* volume is spiky
 *  (CoV > 1.5) — price is discovered elsewhere and flow is arb-dominated. */
export function screen2FlowToxicity(i: Screen2Input): ScreenResult {
  if (i.dailyVolumes.length < MIN_DAILY_SAMPLES) {
    return { value: 0, verdict: 'INSUFFICIENT_DATA', detail: { samples: i.dailyVolumes.length } };
  }
  const cov = coefficientOfVariation(i.dailyVolumes);
  const toxic = i.cexListed && cov > 1.5;
  return {
    value: cov,
    verdict: toxic ? 'WARN' : 'PASS',
    detail: { coefficientOfVariation: cov, cexListed: i.cexListed ? 1 : 0 },
  };
}

// ── Screen 3 — Implied vs. Realized Volatility ─────────────────────────
export interface Screen3Input {
  feeTier: number;
  dailyVolume: number;
  tickLiquidityUsd: number; // USD value of liquidity in the current tick range
  dailyCloses: number[]; // 30 daily closes for realized vol
}

/** IV (Lambert) vs RV (stdev of log returns). PASS iff the pool overcharges
 *  for the risk it sells: IV > RV. Reports ivRvRatio + the daily σ that
 *  Screen 1 consumes. */
export function screen3IvRv(i: Screen3Input): ScreenResult {
  if (i.dailyCloses.length < MIN_DAILY_SAMPLES || i.tickLiquidityUsd <= 0) {
    return {
      value: 0,
      verdict: 'INSUFFICIENT_DATA',
      detail: { samples: i.dailyCloses.length },
    };
  }
  const rvDaily = realizedVolDaily(i.dailyCloses);
  const rvAnnual = annualizeVol(rvDaily);
  const ivDaily = lambertIvDaily(i.feeTier, i.dailyVolume, i.tickLiquidityUsd);
  const ivAnnual = annualizeVol(ivDaily);
  const ivRvRatio = rvAnnual > 0 ? ivAnnual / rvAnnual : 0;
  return {
    value: ivRvRatio,
    verdict: ivAnnual > rvAnnual ? 'PASS' : 'FAIL',
    detail: { rvDaily, rvAnnual, ivDaily, ivAnnual, ivRvRatio },
  };
}

// ── Screen 4 — Hedge Carry (funding) ───────────────────────────────────
export interface Screen4Input {
  fundingRates: number[]; // per-interval funding fractions (30d worth)
  intervalsPerYear?: number; // default 8h settlement → 1095
}

/** Trailing average funding, annualized. PASS iff ≥ 0 (positive funding pays
 *  the short and ADDS to expected edge). */
export function screen4Funding(i: Screen4Input): ScreenResult {
  if (i.fundingRates.length === 0) {
    return { value: 0, verdict: 'INSUFFICIENT_DATA', detail: { samples: 0 } };
  }
  const intervals = i.intervalsPerYear ?? FUNDING_INTERVALS_PER_YEAR;
  const avgPerInterval = mean(i.fundingRates);
  const annualized = avgPerInterval * intervals;
  return {
    value: annualized,
    verdict: annualized >= 0 ? 'PASS' : 'FAIL',
    detail: { avgPerInterval, annualized, samples: i.fundingRates.length },
  };
}

// ── Screen 5 — Liquidation Headroom ────────────────────────────────────
export interface Screen5Input {
  midPrice: number;
  rangeWidth: number; // fraction, e.g. 0.10 for ±10%
  leverage: number; // hedge leverage, e.g. 2
}

/** The short's liquidation price must clear the range's upper edge with
 *  buffer: PASS iff liqPrice > Pb·1.15 (Pb = mid·(1+rangeWidth)). On FAIL the
 *  detail carries the max leverage that would have been safe. */
export function screen5LiqHeadroom(i: Screen5Input): ScreenResult {
  if (i.midPrice <= 0 || i.leverage <= 0) {
    return { value: 0, verdict: 'INSUFFICIENT_DATA', detail: {} };
  }
  const pb = i.midPrice * (1 + i.rangeWidth);
  const threshold = pb * 1.15;
  const liqPrice = shortLiquidationPrice(i.midPrice, i.leverage);
  const safe = liqPrice > threshold;
  return {
    value: liqPrice,
    verdict: safe ? 'PASS' : 'FAIL',
    detail: {
      pb,
      threshold,
      liqPrice,
      headroomRatio: liqPrice / threshold,
      maxSafeLeverage: maxSafeLeverageForShort(i.midPrice, threshold),
    },
  };
}

// ── Screen 6 — Emissions (Aerodrome / Slipstream only) ─────────────────
export interface Screen6Input {
  emissionsApr: number; // gauge emissions APR (fraction), from DefiLlama apyReward
  dailyVolume: number;
  feeTier: number;
  tvl: number;
  sigmaDaily: number;
  aeroPrices: number[]; // 30d AERO price series for the decay warning
}

/** Staked Slipstream LPs earn emissions instead of swap fees, so we run the
 *  LVR breakeven twice — fees-only and emissions-only — and PASS on the
 *  emissions coverage. Also reports the AERO 30d price trend (decay warning). */
export function screen6Emissions(i: Screen6Input): ScreenResult {
  if (i.tvl <= 0 || i.sigmaDaily <= 0) {
    return { value: 0, verdict: 'INSUFFICIENT_DATA', detail: {} };
  }
  const dailyLVR = lvrDaily(i.sigmaDaily);
  const feesDailyReturn = (i.dailyVolume * i.feeTier) / i.tvl;
  const emissionsDailyReturn = i.emissionsApr / TRADING_DAYS_PER_YEAR;
  const feesCoverage = dailyLVR > 0 ? feesDailyReturn / dailyLVR : 0;
  const emissionsCoverage = dailyLVR > 0 ? emissionsDailyReturn / dailyLVR : 0;

  // AERO 30d trend: % change first→last. Negative = decaying reward value.
  const first = i.aeroPrices[0];
  const last = i.aeroPrices[i.aeroPrices.length - 1];
  const aeroTrendPct =
    first !== undefined && last !== undefined && first > 0 ? (last / first - 1) * 100 : 0;

  return {
    value: emissionsCoverage,
    verdict: emissionsCoverage > 1 ? 'PASS' : 'FAIL',
    detail: {
      feesDailyReturn,
      emissionsDailyReturn,
      dailyLVR,
      feesCoverage,
      emissionsCoverage,
      aeroTrendPct,
    },
  };
}

// ── Composite score ────────────────────────────────────────────────────
export interface ExpectedEdgeInput {
  /** Annualized yield the LP actually captures: fee APR (Uniswap) OR
   *  emissions APR (staked Aerodrome). */
  feeOrEmissionsApr: number;
  /** Annualized funding carry (Screen 4 value); negative subtracts. */
  fundingCarryApr: number;
  /** Realized daily vol → annualized LVR drag. */
  sigmaDaily: number;
}

/** Headline metric + default sort: expected annualized edge =
 *  yield + funding carry − LVR. */
export function expectedEdgeAnnual(i: ExpectedEdgeInput): number {
  return i.feeOrEmissionsApr + i.fundingCarryApr - lvrAnnual(i.sigmaDaily);
}
